import { SMSStateItem } from 'store/sms/sms.type';
import BaseNode, { BaseNodeProps, FlowNodeMenuItem } from 'components/FlowBuilder/FlowNodes/BaseNode/BaseNode';
import React, { useCallback, useMemo } from 'react';
import IconSMS from 'pages/FlowBuilder/assets/icons/IconSMS';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import OptionalNodeWrapper from '../OptionalNodeWrapper/OptionalNodeWrapper';
import useJourneyStatus from 'hooks/useJourneyStatus';
import { isEmpty } from 'lodash';
import { useFlowBuilderContext } from 'pages/FlowBuilder/context/flow-builder.context';
import { validateStepAction } from 'validators/ExperienceCanvas/journey.validator';
import { FlowNodeTypes } from '../node.types';
import { TOUCHPOINT_STATUSES } from 'components/ExperienceCanvas/types';
import { ERROR_TYPES } from 'pages/ExperienceCanvas/types';
import { getTooltipIcon } from 'pages/FlowBuilder/helpers/flow-builder.helpers';
import { FlightTooltip } from '@flybits/design-system';
import { JourneyComponentTypes } from 'pages/FlowBuilder/types/flow-builder.types';

import './SMSNode.scss';

const MAIN_CLASS = 'sms-node';
const CLASSES = {
  LABEL: `${MAIN_CLASS}__label`,
  LABEL_TOOLTIP: `${MAIN_CLASS}__label__tooltip`,
  LABEL_DISABLED: `${MAIN_CLASS}__label--disabled`,
  LABEL_TEXT: `${MAIN_CLASS}__label__text`,
  LABEL_NAME: `${MAIN_CLASS}__label__name`,
  LABEL_BUTTON: `${MAIN_CLASS}__label__button`,
};

type SMSNodeProps = {
  data: {
    sms: SMSStateItem;
    stepIndex: number;
  };
};

function SMSNode({ data: { sms, stepIndex } }: SMSNodeProps) {
  const { isJourneyLocked } = useJourneyStatus();
  const dependencyGraph = useSelector((state) => state.te.journey.dependencyGraph);
  const {
    sidePanels: { sms: smsSidePanel },
    modals: {
      rename: { setData: setRenameModalData },
    },
  } = useFlowBuilderContext();

  const { errorStatus, errorMessage } = useSelector((state) => {
    const { errorStatus, errors } = validateStepAction(state, stepIndex, FlowNodeTypes.SMS, sms.refId);
    // currently only revealing the first error on a node.
    const errorMessage = errors[0]?.err ?? '';

    if (errorStatus === '') {
      return { errorStatus: TOUCHPOINT_STATUSES.COMPLETE, errorMessage };
    } else if (errorStatus === ERROR_TYPES.INCOMPLETE) {
      return { errorStatus: TOUCHPOINT_STATUSES.INCOMPLETE, errorMessage };
    } else {
      return { errorStatus: TOUCHPOINT_STATUSES.ERROR, errorMessage };
    }
  });

  const isOptional = sms.isOptional;
  const isOptionalSMSEnabled = !!sms.enableOptionalNode;

  const isConfigured = errorStatus !== TOUCHPOINT_STATUSES.INCOMPLETE;

  const handleSetupSMS = useCallback(() => {
    smsSidePanel.setStepIndex(stepIndex);
    smsSidePanel.setSMSContext({
      isOpen: true,
      isViewOnly: isJourneyLocked,
      sms,
    });
  }, [sms, smsSidePanel, stepIndex, isJourneyLocked]);

  const handleRenameSMS = useCallback(() => {
    setRenameModalData({
      entity: sms,
      entityType: JourneyComponentTypes.SMS,
      entityNodeName: sms.name,
      stepIndex,
    });
  }, [sms, setRenameModalData, stepIndex]);

  const baseNodeProps = useMemo<Omit<BaseNodeProps, 'children'>>(() => {
    const menuItems: FlowNodeMenuItem[] = [];
    if (isConfigured) {
      if (isJourneyLocked) {
        menuItems.push({
          icon: 'eyeOpenOutline',
          label: 'View SMS',
          onClick: handleSetupSMS,
          iconRight: getTooltipIcon(errorStatus),
          iconRightTooltip: errorMessage,
        });
      } else {
        menuItems.push(
          {
            icon: 'editOutline',
            label: 'Edit SMS',
            onClick: handleSetupSMS,
            iconRight: getTooltipIcon(errorStatus),
            iconRightTooltip: errorMessage,
          },
          {
            icon: 'textBoxWithCorners',
            label: 'Rename',
            onClick: handleRenameSMS,
          },
        );
      }
    }

    return {
      className: `${MAIN_CLASS} ${isConfigured ? '' : `${MAIN_CLASS}--unconfigured`}`,
      Icon: IconSMS,
      menuItems,
    };
  }, [errorMessage, errorStatus, handleSetupSMS, isConfigured, handleRenameSMS, isJourneyLocked]);

  const renderSMSNode = () => (
    <div
      className={`${CLASSES.LABEL} ${
        shouldShowOptionalNodeWrapper && !isOptionalSMSEnabled ? CLASSES.LABEL_DISABLED : ''
      }`}
    >
      <div className={CLASSES.LABEL_TEXT}>Send an SMS</div>
      {isConfigured ? (
        <FlightTooltip description={sms.name || 'SMS Template'} className={CLASSES.LABEL_TOOLTIP}>
          <div className={CLASSES.LABEL_NAME}>{sms.name || 'SMS Template'}</div>
        </FlightTooltip>
      ) : (
        <button className={CLASSES.LABEL_BUTTON} onClick={handleSetupSMS}>
          <span>+</span>
          Set up SMS
        </button>
      )}
    </div>
  );

  const shouldShowOptionalNodeWrapper = useMemo(() => {
    if (isJourneyLocked) {
      return false;
    }

    let showWrapper = false;

    if (isEmpty(dependencyGraph?.[sms.refId]?.isRefBy) && isOptional) {
      showWrapper = true;
    }

    return showWrapper;
  }, [isJourneyLocked, dependencyGraph, sms.refId, isOptional]);

  return shouldShowOptionalNodeWrapper ? (
    <OptionalNodeWrapper isEnabled={isOptionalSMSEnabled} onToggle={() => null}>
      <BaseNode {...baseNodeProps} isOptional isDisabled={!isOptionalSMSEnabled}>
        {renderSMSNode()}
      </BaseNode>
    </OptionalNodeWrapper>
  ) : (
    <BaseNode {...baseNodeProps}>{renderSMSNode()}</BaseNode>
  );
}

export default SMSNode;
